import React from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { PACKAGES } from "../data/site";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export default function Pricing() {
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-24 md:py-32 bg-[#050505]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="max-w-3xl mb-16">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF3B30] mb-4">
            — Packages
          </div>
          <h2 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tighter leading-[0.9]">
            Plans that scale
            <br />
            <span className="text-outline">with your ambition.</span>
          </h2>
          <p className="mt-6 text-white/60 text-base md:text-lg leading-relaxed">
            Transparent monthly retainers. No lock‑in lies. Custom quotes on
            request — we love the weird briefs.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {PACKAGES.map((p, idx) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: idx * 0.08 }}
              data-testid={`pricing-card-${p.id}`}
              className={`relative p-8 md:p-10 flex flex-col ${
                p.highlighted
                  ? "bg-[#0a0a0a] border border-[#FF3B30] shadow-[0_0_40px_-15px_rgba(255,59,48,0.6)]"
                  : "bg-[#111] border border-white/10"
              } transition-all duration-500 hover:-translate-y-1`}
            >
              {p.highlighted && (
                <div className="absolute -top-3 left-8 px-3 py-1 bg-[#FF3B30] text-[10px] uppercase tracking-[0.25em] font-bold">
                  Most popular
                </div>
              )}

              <div className="flex items-baseline justify-between">
                <h3 className="font-display text-3xl font-extrabold uppercase">
                  {p.name}
                </h3>
                <span className="text-white/40 text-xs uppercase tracking-[0.25em]">
                  0{idx + 1}
                </span>
              </div>

              <p className="mt-3 text-white/60 text-sm">{p.blurb}</p>

              <div className="mt-8 flex items-end gap-2">
                <div className="text-xs text-white/50 uppercase tracking-[0.2em] mb-2">
                  RM
                </div>
                <div className="font-display text-5xl md:text-6xl font-black leading-none">
                  {p.priceMYR.toLocaleString()}
                </div>
                <div className="text-white/50 text-sm mb-2">{p.cadence}</div>
              </div>

              <div className="hline my-8" />

              <ul className="space-y-4 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex gap-3 text-sm text-white/75">
                    <span
                      className={`mt-0.5 w-5 h-5 flex items-center justify-center shrink-0 ${
                        p.highlighted
                          ? "bg-[#FF3B30] text-white"
                          : "border border-white/15 text-white/60"
                      }`}
                    >
                      <Check size={12} strokeWidth={3} />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>

              <button
                data-testid={`pricing-cta-${p.id}`}
                onClick={() => scrollTo("book")}
                className={`mt-10 inline-flex items-center justify-between gap-4 py-4 px-5 uppercase tracking-[0.2em] text-xs font-bold transition-all ${
                  p.highlighted
                    ? "bg-[#FF3B30] text-white hover:bg-[#FF5722]"
                    : "border border-white/15 text-white hover:border-[#FF3B30] hover:text-[#FF3B30]"
                }`}
              >
                {p.cta}
                <ArrowRight size={16} />
              </button>
            </motion.div>
          ))}
        </div>

        <div className="mt-12 text-center text-sm text-white/40">
          Need a custom scope?{" "}
          <button
            onClick={() => scrollTo("contact")}
            data-testid="pricing-custom-quote"
            className="text-[#FF3B30] hover:underline uppercase tracking-[0.2em]"
          >
            Request a custom quote →
          </button>
        </div>
      </div>
    </section>
  );
}
